import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { IconDownload } from '@tabler/icons-react'
import { isEmpty, isNil } from 'lodash'

import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { Button } from '@/components/buttons/Button'
import { Card } from '@/components/cards/Card'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { Loader } from '@/components/loaders/Loader'
import { Pill } from '@/components/pills/Pill'
import { downloadCsvTemplate } from '@/components/forms/CsvInstructions/helpers'

import { fetchResults } from '@/reducers/costimizer/costimizer.actions'
import { ACTIVE_STATUSES, selectRun } from '@/reducers/costimizer/costimizer.redux'

import { ApplyMigration } from './ApplyMigration'
import { ApplyPromptButton } from './ApplyPromptButton'
import { StartMigrationButton } from './StartMigrationButton'
import {
  canApply,
  costChangePct,
  criteriaPassingAt,
  formatCostChange,
  isAnswered,
  isMissingItsRewrite,
  KIND,
  migratableSubjects,
  RECOMMENDED_TOLERANCE,
  resultRows,
  subjectRows,
  VERDICT,
} from '../Costimizer.helpers'

const formatAccuracy = (value) => (isNil(value) ? '—' : `${(value * 100).toFixed(1)}%`)
const formatMargin = (value) =>
  isNil(value) ? '—' : `${value > 0 ? '+' : ''}${value.toFixed(1)} pts`

// One column rather than two, with the difference beside it: three columns where the third was the other two
// subtracted was the biggest source of width here.
const formatScorePair = (own, cheap) =>
  isNil(own) && isNil(cheap) ? '—' : `${formatAccuracy(own)} → ${formatAccuracy(cheap)}`

const STALE_EXPLANATION =
  'This prompt has been edited since it was tested, so the result describes text that is no longer live. It will not be applied — re-test it.'

const UNEXPECTED_EXPLANATION =
  'The server sent a result this page does not recognise, so there is nothing to read here. That is a bug on our side rather than a finding about the prompt.'

const MISSING_REWRITE_EXPLANATION =
  'This prompt held on a rewrite, but the rewritten text did not come back with the result, so there is nothing to publish. Re-test it.'

const MIGRATED_EXPLANATION =
  'This prompt is live on the model this result measured it on, so the migration has been applied. Nothing left to do here.'

const WITHHELD_EXPLANATION =
  'The cheaper model agreed with production but stopped filling in one of the evidence fields an agent reads when disputing a score, so this result was not counted. No threshold applies it.'

/**
 * What went wrong, by the kind of failure the action recorded.
 *
 * ⚠ One sentence for every kind sends people the wrong way: a failed APPLY said "could not load the results",
 * so the prompts looked unreadable when in fact nothing had been published.
 */
const FAILED = {
  resultsFailed: 'Could not load the migration results.',
  applyFailed: 'Could not apply the migration — nothing was published. Try again.',
  startFailed: 'Could not start the migration test.',
  statusFailed: 'Could not check whether a migration is running.',
}

/**
 * Every prompt on a scorecard, with what a migration found for it.
 *
 * One table: the prompts and the results describe the same things. A prompt nobody has tested says so in its
 * status column, and is the only kind that offers a test.
 *
 * ⚠ THE THRESHOLD LIVES HERE, not in `ApplyMigration`. A prompt's status and whether applying would take it
 * are the same question, so both have to be answered from the same number — otherwise a row can read "below
 * threshold" while the button below it is about to publish that very prompt.
 */
export const ScorecardResults = ({ scorecard }) => {
  const dispatch = useDispatch()
  const run = useSelector(selectRun(scorecard.scorecardConfigId))
  const [tolerancePct, setTolerancePct] = useState(RECOMMENDED_TOLERANCE)

  // This scorecard's own status once it has one, else the one the server reported. `subjectKeys` takes the
  // same fallback for the same reason: on first render nothing has polled this run, and reading its absence
  // as "covers everything" marks every row as being tested.
  const status = run.status ?? scorecard.run.status
  // Null all the way down when nothing knows: `subjectRows` then marks no row, rather than reading unknown
  // as "the whole scorecard" and marking every one of them.
  const subjectKeys = run.subjectKeys ?? scorecard.run.subjectKeys ?? null
  // Normalised to null, because it is compared with what the results in hand were fetched for — and that is
  // stored as null. Left undefined, "no run" never equals itself and the fetch below repeats forever.
  const flowRunId = run.flowRunId ?? scorecard.run.flowRunId ?? null
  const runIsGoing = ACTIVE_STATUSES.includes(status)

  // Fetch when there are no results, or when the results in hand are a previous run's — re-testing one
  // prompt starts a new flow run, and its numbers replace the ones on screen.
  //
  // A failed fetch leaves no results and nothing loading, which is the same condition that asks for a fetch —
  // so an error has to stop it, or a broken endpoint gets hammered in a loop.
  const needsResults = isNil(run.results) || run.resultsFor !== flowRunId

  // ⭐ NOT GATED ON A RUN BEING FOUND, only on one not being in flight. The results live in the database
  // while the run is a Prefect flow, which goes missing when the scorecard is saved, when Prefect ages it
  // out, and while Prefect is unreachable. Waiting for a `done` run hides results that are sitting there.
  useEffect(() => {
    if (!runIsGoing && needsResults && !run.resultsLoading && isNil(run.error)) {
      dispatch(fetchResults(scorecard.scorecardConfigId, flowRunId))
    }
  }, [
    dispatch,
    flowRunId,
    runIsGoing,
    needsResults,
    run.error,
    run.resultsLoading,
    scorecard.scorecardConfigId,
  ])

  const criteria = run.results?.criteria ?? []

  // The one list that decides everything the threshold touches: each row's status and what the button
  // applies. The results themselves rather than their keys — each row carries the very result object, and a
  // key names a version of the prompt that a scorecard save moves.
  const applying = criteriaPassingAt(criteria, tolerancePct)

  // Flattened onto the row, because AdvancedTable sorts and searches on the accessor's own value. The
  // rewritten prompt goes in `expandedContent`, which is only set where there IS one — a row without it
  // gets no chevron, so the table says which prompts were rewritten without a column for it.
  const rows = subjectRows(scorecard.subjects, criteria, runIsGoing ? subjectKeys : null).map(
    (row) => ({
      ...row,
      selfAgreement: row.result?.selfAgreement,
      bestAccuracy: row.result?.bestAccuracy,
      marginPct: row.result?.marginPct,
      costChange: costChangePct(row.result),
      verdict: row.result?.verdict,
      expandedContent: row.result?.newPrompt ? (
        <>
          <p className="muted-text">
            {`Rewritten for ${row.result.newModel ?? 'the cheaper model'}`}
          </p>
          <pre className="costimizer-prompt">{row.result.newPrompt}</pre>
        </>
      ) : undefined,
    })
  )

  const columns = [
    {
      accessor: 'name',
      label: 'Prompt',
      isSearchable: true,
      format: (value, row) => (
        <span className="flex-align-center small-gap">
          {value}
          {row.kind === KIND.ELIGIBILITY && <Pill>eligibility</Pill>}
        </span>
      ),
    },
    { accessor: 'model', label: 'Model', noWrap: true },
    {
      accessor: 'selfAgreement',
      label: 'Score (own → cheap)',
      // Two numbers in one cell, so sorting it would be ambiguous about which. The two columns worth
      // sorting by are next to it.
      isSortable: false,
      format: (value, row) => formatScorePair(value, row.bestAccuracy),
    },
    { accessor: 'marginPct', label: 'Difference', format: formatMargin },
    // Shown whatever the threshold would take. It is a measured fact about the prompt, and someone deciding
    // whether to loosen the threshold needs to know what loosening would buy — hiding it until they loosen
    // is circular.
    { accessor: 'costChange', label: 'Cost change', format: formatCostChange },
    {
      accessor: 'verdict',
      label: 'Status',
      isSortable: false,
      format: (value, row) => <StatusCell row={row} isApplying={applying.includes(row.result)} />,
    },
    {
      accessor: 'actions',
      label: '',
      isSortable: false,
      format: (value, row) => (
        <span className="flex-align-center small-gap">
          {/* ⭐ One prompt, whatever the threshold says — see `ApplyPromptButton`. Offered on any row that
              CAN be published, so the threshold below stays a convenience rather than the only way in. */}
          {canApply(row.result) && !runIsGoing && (
            <ApplyPromptButton
              scorecardConfigId={scorecard.scorecardConfigId}
              criterion={row.result}
            />
          )}
          {/* Offered whenever there is something to migrate and nothing in flight. A prompt that already
              HAS a result is the commonest thing anyone wants to re-test, after an edit or after the tool
              itself changed; hidden during a run, which would be a second run on one scorecard. */}
          {row.migratable && !runIsGoing && (
            <StartMigrationButton
              scorecardConfigId={scorecard.scorecardConfigId}
              subjectKeys={[row.key]}
              nSubjects={1}
              perPromptMinutes={scorecard.perPromptMinutes}
              label={row.result ? 'Re-test' : 'Test this prompt'}
            />
          )}
          {!isEmpty(row.result?.calls) && (
            <Button
              small
              basic
              square
              aria-label="Download per-call labels"
              tooltip="Download per-call labels"
              onClick={() => downloadCsvTemplate(resultRows(row.result), `${row.key}-labels.csv`)}
            >
              <IconDownload />
            </Button>
          )}
        </span>
      ),
    },
  ]

  return (
    <Card className="costimizer-results" data-testid="costimizer-scorecard-results">
      <h4>
        Prompts on this scorecard
        {run.results && (
          <span className="muted-text">
            {` ${run.results.nMeasured} measured · ${run.results.nRefused} refused`}
            {/* Only when there are any: an unfinished prompt is unusual and worth noticing, and a
                permanent "0 did not finish" would train someone to stop reading the line. */}
            {run.results.nIncomplete > 0 && ` · ${run.results.nIncomplete} did not finish`}
          </span>
        )}
      </h4>

      {run.error && (
        <ErrorMessage
          content={FAILED[run.error.kind] ?? 'Something went wrong with this migration.'}
        />
      )}

      {run.resultsLoading && <Loader centered label="Loading results" />}

      <AdvancedTable
        testid="costimizer-prompts-table"
        rows={rows}
        columns={columns}
        index="key"
        expandable
        noDataHeader="Nothing to migrate"
        noDataText="Every prompt on this scorecard is already on the target model."
      />

      {rows.some((row) => row.result?.labelsWithheld) && (
        <p className="muted-text">
          This organization&apos;s call content is encrypted, so the download carries each
          call&apos;s id and whether every run agreed with the reference, but not the answers
          themselves.
        </p>
      )}

      {/* Whenever there is something measured to apply and nothing in flight — not "when a run says done",
          which a saved scorecard or an unreachable Prefect leaves us without. An empty result set is a
          scorecard nobody has tested, where an apply control would be furniture. */}
      {!isEmpty(criteria) && !runIsGoing && (
        <ApplyMigration
          scorecardConfigId={scorecard.scorecardConfigId}
          subjects={migratableSubjects(scorecard.subjects)}
          criteria={criteria}
          tolerancePct={tolerancePct}
          onToleranceChange={setTolerancePct}
        />
      )}
    </Card>
  )
}

/**
 * Where a prompt stands: already migrated, never tested, or whatever the run found read at the threshold now
 * chosen.
 *
 * "Not tested" is a status rather than an empty cell, because a blank row reads as something failing to load.
 *
 * ⚠ AN UNFINISHED RUN IS NOT A FAILED ONE. `incomplete` means a step errored, so the prompt has no answer,
 * and `not_measurable` that no bar was established — rendering either as a measured failure would present a
 * measurement nobody made, on the row where someone decides whether to migrate. An unrecognised verdict says
 * the page and the server have drifted apart, which is also not a finding about the prompt.
 *
 * @param {boolean} props.isApplying whether the current threshold would publish this prompt — the same list
 *   the apply button uses, so the two cannot contradict each other
 */
const StatusCell = ({ row, isApplying }) => {
  // Before anything the run found, because it is about this prompt NOW. Testing one prompt puts the whole
  // scorecard into `running`, so a row that says nothing here is indistinguishable from the one being tested.
  if (row.running) {
    return <Pill dataTestId="costimizer-subject-running">Testing…</Pill>
  }

  // ⚠ BEFORE `stale`, which is true here too: what is live is this result's own prompt, and applying is
  // exactly what makes the live prompt differ from the incumbent it was measured against. The other way
  // round, a successful apply reports as somebody having edited the prompt behind our back.
  if (row.result?.migrated) {
    return (
      <Pill dataTestId="costimizer-subject-migrated" tooltip={MIGRATED_EXPLANATION}>
        Migrated
      </Pill>
    )
  }

  if (!row.result) {
    // Nothing measured, and nothing to measure: already on the model a migration would move it to, whether
    // or not it got there through this tool.
    return <span className="muted-text">{row.onTarget ? 'Nothing to migrate' : 'Not tested'}</span>
  }

  if (row.result.stale) {
    return (
      <Pill caution tooltip={STALE_EXPLANATION}>
        Prompt edited since
      </Pill>
    )
  }

  if (row.result.refusalReason) {
    return <span className="muted-text">Not enough calls</span>
  }

  if (row.result.verdict === VERDICT.INCOMPLETE) {
    return <Pill caution>Test did not finish</Pill>
  }

  if (row.result.verdict === VERDICT.NOT_MEASURABLE) {
    return <span className="muted-text">No score to compare</span>
  }

  if (!isAnswered(row.result)) {
    return (
      <Pill caution tooltip={UNEXPECTED_EXPLANATION}>
        Unexpected result
      </Pill>
    )
  }

  if (isMissingItsRewrite(row.result)) {
    return (
      <Pill caution tooltip={MISSING_REWRITE_EXPLANATION}>
        Rewrite unavailable
      </Pill>
    )
  }

  // Answered, and no margin: the score was measured and then REFUSED, because the cheap model stopped
  // filling in an evidence field. "Below threshold" would read as a near miss on agreement, which it is not.
  if (isNil(row.result.marginPct)) {
    return (
      <Pill caution tooltip={WITHHELD_EXPLANATION}>
        Dropped an evidence field
      </Pill>
    )
  }

  return <Pill>{isApplying ? 'Can migrate' : 'Below threshold'}</Pill>
}
